<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <v-row class="flex-grow-0">
            <v-col cols="12" sm="3">
                <v-select v-model="customerSelector.selectedItem" base-color="primary" :ref="el => customerSelector.component = el" :label="$t('field.customer')"
                    @update:model-value="onCustomerSelectorChange" variant="outlined" density="comfortable" :items="customers" item-title="name" item-value="id"
                    :disabled="controlsDisabled" hide-details return-object />
            </v-col>

            <v-col cols="12" sm="4">
                <v-select v-model="batchSelector.selectedItem" base-color="primary" :ref="el => batchSelector.component = el" variant="outlined" density="comfortable" :items="batchItems"
                item-title="name" item-value="value" :disabled="controlsDisabled || customerSelector.selectedItem == ''"
                @update:model-value="onBatchSelectorChange" hide-details return-object/>
            </v-col>

            <v-col cols="12" sm="2">
                <v-date-input v-model="startDate" :label="$t('field.startDate')" base-color="primary"
                variant="outlined" density="comfortable" :disabled="dateControlsDisabled" hide-details />
            </v-col>

            <v-col cols="12" sm="2">
                <v-date-input v-model="endDate" :label="$t('field.endDate')" base-color="primary" 
                variant="outlined" density="comfortable" :disabled="dateControlsDisabled" hide-details />
            </v-col>

            <v-col cols="12" sm="1" class="d-flex justify-center">
                <v-btn :icon="icons.mdiRefresh" variant="tonal" color="secondary"
                density="default" @click="refreshLogBatchGrid" />
            </v-col>
        </v-row>

        <AgGrid class="flex-grow-1" ref="logBatchesAgGridRef" :fetchUrl="logBatchesFetchUrl"
        :columnDefs="logBatchesColumnDefs" :gridParams="logBatchesGridParams"
        :refresh="logBatchesRefresh" :refreshCallback="logBatchesRefreshCallBack" :domLayout="domLayout"/>
    </div>

    <!-- Logs Dialog -->
    <v-dialog v-model="logDialogVisible" scrollable class="w-50 h-75">
        <v-card>
            <v-card-title class="d-flex align-center pa-4">
                {{ $t('dialog.logs') }}
                <v-spacer />
                <v-btn size="small" variant="text" :prepend-icon="icons.mdiContentCopy" @click="copyAllText">{{ $t('action.copyAll') }}</v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="d-flex">
                <div ref="logBatchesTerminal" id="logBatchesTerminal" class="terminal w-100 d-print-block">
                    <span v-html="logDialogContent"></span>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="justify-end">
                <v-btn variant="text" @click="logDialogVisible = false">{{ $t('action.cancel') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Download Files Dialog -->
    <v-dialog v-model="downloadFileDialogVisible" class="w-33">
        <v-card>
            <v-card-title class="d-flex align-center pa-4">
                {{ $t('dialog.files') }}
                <v-spacer />
                <v-btn size="small" variant="text" :prepend-icon="icons.mdiDownload" @click="downloadAll">{{ $t('action.downloadAll') }}</v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text>
                <v-list>
                    <v-list-item v-for="file in downloadFiles" :key="file.guid" :title="file.name" :subtitle="(file.sizeKo / 1024).toFixed(2) + ' Ko'" @click="downloadFile(file)">
                        <template #append>
                            <v-icon :icon="icons.mdiDownload" />
                        </template>
                    </v-list-item>
                </v-list>
            </v-card-text>

            <v-divider />

            <v-card-actions class="justify-end">
                <v-btn variant="text" @click="downloadFileDialogVisible = false">{{ $t('action.cancel') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { inject, onBeforeUnmount, onMounted, reactive, ref, watch, useTemplateRef } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import logBatchesJson from '@/components/json/batches/logBatches.json';
    import { mdiRefresh, mdiContentCopy, mdiDownload } from '@mdi/js';
    import { toast } from 'vue3-toastify';
    import { useControlStore } from '@/stores/controlStore';
    import { useDateStore } from '@/stores/dateStore';
    import { useUserStore } from '@/stores/userStore';

    const props = defineProps({
        customers: {
            type: Array,
            default: () => [],
        },
    });

    const startProgress = inject('startProgress');
    const stopProgress = inject('stopProgress');
    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const controlStore = useControlStore();
    const dateStore = useDateStore();
    const userStore = useUserStore();

    const logBatchesTerminal = ref(null);

    const icons = {
        mdiRefresh,
        mdiContentCopy,
        mdiDownload,
    };
    const batchItems = ref([]);

    const domLayout = 'normal';
    const logBatches = logBatchesJson;


    const customerSelector = reactive({
        selectedItem : null,
        component : null,
    });

    const batchSelector = reactive({
        selectedItem: null,
        component: null,
    });

    const startDate = ref(dateStore.toISODate(dateStore.subtractDays(new Date(), 7)));
    const endDate = ref(dateStore.toISODate(new Date()));

    const logBatchesSelectingAll = ref(false);
    const controlsDisabled = ref(false);
    const dateControlsDisabled = ref(false);
    const logBatchesGridRef = useTemplateRef('logBatchesAgGridRef');
    const logBatchesFetchUrl = 'GetLogBatches';
    const logBatchesColumnDefs = [
        {
            field: 'customerReference',
            headerName: translate('customer', 'column'),
        },
        {
            field: 'batchName',
            headerName: translate('name', 'column'),
        },
        {
            field: 'id',
        },
        {
            field: 'createdDate',
            type: 'date',
            sort: 'desc',
            headerName: translate('createdDate', 'column'),
        },
        {
            field: 'state',
            type: 'logBatchStatus',
            headerName: translate('state', 'column'),
        },
        {
            field: 'comment',
            headerName: '',
            type: 'logAction',
        },
        {
            field: 'download',
            headerName: '',
            type: 'logAction',
        },
    ];
    const logBatchesGridParams = reactive({
        token: userStore.getUser().token,
        customerReference: null,
        batchName: null,
        startDate: null,
        endDate: null,
    });

    const logBatchesRefresh = ref(false);

    const logDialogVisible = ref(false);
    const logDialogContent = ref('');

    const downloadFileDialogVisible = ref(false);
    const downloadFiles = ref([]);

    watch(startDate, () => {
        logBatchesGridParams.startDate = dateStore.toISODate(startDate.value) || null;

        if (startDate.value && logBatchesGridRef.value.gridRef.api != null && logBatchesRefresh.value === false){
            refreshLogBatchGrid();
        }
    });

    watch(endDate, () => {
        logBatchesGridParams.endDate = dateStore.toISODate(endDate.value) || null;

        if (endDate.value && logBatchesGridRef.value.gridRef.api != null && logBatchesRefresh.value === false){
            refreshLogBatchGrid();
        }
    });

    logBatchesGridParams.startDate = startDate.value;
    logBatchesGridParams.endDate = endDate.value;

    onMounted(() => {
        window.addEventListener('jobsLogBatches:openLog', onOpenLog);
        window.addEventListener('jobsLogBatches:filesFound', onFilesFound);
        batchItems.value = initBatchItems();
    });

    onBeforeUnmount(() => {
        window.removeEventListener('jobsLogBatches:openLog', onOpenLog);
        window.removeEventListener('jobsLogBatches:filesFound', onFilesFound);
    });

    function initBatchItems(){
        let items = [{ name: translate('all', 'toast'), value: 'all' }];

        for (let group of Object.keys(logBatches)) {
            for (let logBatch of logBatches[group]) {
                items.push({ name: `${logBatch.name} (${logBatch.value})`, value: logBatch.value});
            }
        }
        return items;
    }

    function onActivate() {
        if (customerSelector.selectedItem === '' || customerSelector.selectedItem === null){
            setActiveCustomer();
            batchSelector.component.$emit('update:modelValue', logBatches.general.first());
        }

        if(customerSelector.selectedItem !== '' && customerSelector.selectedItem != null && batchSelector.selectedItem !== '' && batchSelector.selectedItem !== null){
            //if all our selector are binded
            //we trigger the "updated" hook on ag-grid by changing the props logbatchesRefresh value
            refreshLogBatchGrid();
        }
    }

    function onCustomerSelectorChange(){
        logBatchesGridParams.customerReference = customerSelector.selectedItem.reference;
        //if we're ALL MODE
        if(customerSelector.selectedItem.reference === 'all'){
            toast.info(translate('loadingAllBatchesAllCustomers', 'toast'), {theme: 'colored'})
            logBatchesSelectingAll.value = true;
            //is batch selector not on all ?
            if(batchSelector.selectedItem?.value !== 'all'){
                // then we emit the value to the selector
                batchSelector.component.$emit('update:modelValue', batchItems.value.first());
            }
        }
        else{
            //not ALL MODE we can store the customer in localstorage
            logBatchesSelectingAll.value = false;
            controlStore.setActiveCustomer(customerSelector.selectedItem);
            //is batch selector still on 'all' value
            if(batchSelector.selectedItem?.value === 'all'){
                //then we give it the first value of the json list of batches
                batchSelector.component.$emit('update:modelValue', logBatches.general.first());
            }
        }

        if(logBatchesGridRef.value.gridRef.api != null && logBatchesRefresh.value === false){
            refreshLogBatchGrid();
        }
    }

    function onBatchSelectorChange(){
        logBatchesGridParams.batchName = batchSelector.selectedItem.value;

        if(batchSelector.selectedItem.value === 'all'){
            logBatchesSelectingAll.value = true;
            setAllBatchesFilters();
            if(customerSelector.selectedItem.reference !== 'all'){
                customerSelector.component.$emit('update:modelValue', props.customers.first());
            }
        }
        else{
            logBatchesSelectingAll.value = false;
            if(customerSelector.selectedItem.reference == 'all'){
                setActiveCustomer();
            }
        }

        if(logBatchesGridRef.value.gridRef.api != null && logBatchesRefresh.value === false){
            refreshLogBatchGrid();
        }
    }


    function setAllBatchesFilters() {
        logBatchesGridParams.customerReferences = props.customers.filter(c => !c.demo).map(c => c.reference).filter(r => r != 'all');
        logBatchesGridParams.batchNames = logBatches.general.map(lb => lb.value).concat(logBatches.in.map(lb => lb.value)).concat(logBatches.out.map(lb => lb.value));

        // SET DATES TO LAST 24 HOURS
        startDate.value = dateStore.subtractDays(new Date(), 1);
        endDate.value = new Date();
    }

    function setActiveCustomer() {
        let activeCustomer = controlStore.getActiveCustomer() !== null ? controlStore.getActiveCustomer() : null;

        if (activeCustomer != null){
            customerSelector.component.$emit('update:modelValue', activeCustomer);
        }
        else{
            customerSelector.component.$emit('update:modelValue', props.customers.filter(e => e.reference !== 'all').first());
        }
    }

    function refreshLogBatchGrid() {
        if (logBatchesGridParams.customerReference != null && logBatchesGridParams.customerReference != '' && logBatchesGridParams.batchName != null && logBatchesGridParams.batchName != ''){
            startProgress();
            logBatchesGridRef.value.gridRef.api.setGridOption('loading', true);
            controlsDisabled.value = true;
            dateControlsDisabled.value = true;

            //trigger the hook "updated" in AgGrid
            logBatchesRefresh.value = true;
        }
    }

    function logBatchesRefreshCallBack() {
        logBatchesRefresh.value = false;
        controlsDisabled.value = false;
        //if we're still in ALL MODE we keep dates controls disabled
        if(!logBatchesSelectingAll.value){
            dateControlsDisabled.value = false;
        }
        stopProgress();
        logBatchesGridRef.value.gridRef.api.setGridOption('loading', false);

    }

    function onOpenLog(event) {
        logDialogContent.value = event.detail.comment;
        logDialogVisible.value = true;
    }

    function onFilesFound(event) {
        downloadFiles.value = event.detail.files;
        downloadFileDialogVisible.value = true;
    }

    function copyAllText() {
        navigator.clipboard.writeText($(logBatchesTerminal.value).html().replace(/<br>/g, '\n\r').replace(/<\/?[^>]+(>|$)/g, ''));
        toast.info(translate('copiedInClipboard', 'action'), {theme: 'colored'});
    }

    function downloadFile(file) {
        var customerReference = customerSelector.selectedItem.reference ?? controlStore.getActiveCustomer()?.reference
        $.downloadFile(file.guid, null, file.name, urlWebAPI, customerReference, userStore.getUser().token);
    }

    function downloadAll() {
        downloadFiles.value.forEach(file => downloadFile(file));
    }

    // ViewJobs.vue calls onActivate() through a template ref — only visible through
    // defineExpose() on a <script setup> component.
    defineExpose({ onActivate });
</script>
